import React from 'react';
import PropTypes from 'prop-types';
import Card from 'reactstrap/lib/Card';
import { get } from 'lodash';
import { InventoryEntities } from 'client/data/models/inventory';
import { DATA_PATH } from 'client/engagement-handlers/inventory-engagement-handler/constants';
import { SOURCES } from 'client/utils/image-helpers';
import { buildVinPreviewSrpLink } from 'site-modules/shared/utils/srp-link-constructor';
import { getVehicleName } from 'site-modules/shared/utils/inventory/usurp-inventory-card';
import { getUsurpImages } from 'site-modules/shared/utils/inventory/srp-images';
import { getPriceString } from 'site-modules/shared/utils/price-utils';
import { numberWithCommas } from 'site-modules/shared/utils/string';
import { isDeliveryEligible, isDriveway, isInstockOnline } from 'site-modules/shared/utils/dealer-details-utils';
import { isNew } from 'site-modules/shared/utils/inventory-utils/is-new';
import { Link } from 'site-modules/shared/components/link/link';
import { Image } from 'site-modules/shared/components/image/image';
import { AspectRatioContainer } from 'site-modules/shared/components/aspect-ratio-container/aspect-ratio-container';

import './mini-inventory-card.scss';

const IN_STOCK_ONLINE = 'In-stock online';

export function MiniInventoryCard({
  vehicleParams: { makeSlug, modelSlug, year },
  inventoryVehicle,
  radius,
  zipCode,
  isInternational,
}) {
  const { vin, type, dealerInfo } = inventoryVehicle;
  const isNewType = isNew(type);

  const vdpPreviewSrpLink = buildVinPreviewSrpLink(vin, {
    make: makeSlug,
    model: modelSlug,
    year,
  });

  const { photoUrls } = getUsurpImages(inventoryVehicle);
  const photoUrl = photoUrls?.[0];

  const [firstLine, secondLine] = getVehicleName(inventoryVehicle, true, true);
  const vehicleName = secondLine || firstLine;

  const displayPrice = get(inventoryVehicle, 'prices.displayPrice');

  const parentDealershipName = get(inventoryVehicle, DATA_PATH.PARENT_DEALERSHIP_NAME, '');
  const isDrivewayDealer = isDriveway(parentDealershipName);
  const isDeliveryAvailable = isDeliveryEligible(inventoryVehicle, zipCode);
  const distance = Math.round(get(dealerInfo, 'distance', 0));

  const isInStockOnline =
    (!isNewType || isDrivewayDealer) && isInstockOnline({ distance, radius, isDeliveryAvailable });

  return (
    <Card className="mini-inventory-card rounded-12 bg-white overflow-hidden pos-r h-100">
      <AspectRatioContainer className="mb-0_5" aspectRatio="3:2">
        <Image imageId={photoUrl} source={SOURCES.EDMUNDSCDN} lazy />
      </AspectRatioContainer>
      <div className="d-flex flex-column justify-content-between h-100 px-0_75 pb-0_5">
        <Link
          to={vdpPreviewSrpLink}
          className="link fw-bold text-primary-darker mb-0_75"
          data-tracking-id="view_vin_preview"
        >
          {vehicleName}
        </Link>
        <div>
          <div className="small mb-0_25">
            MSRP: <span className="size-24 fw-bold">{getPriceString(displayPrice)}</span>
          </div>
          {!isInternational && (
            <div className="medium text-cool-gray-40">
              <i className="icon-location small me-0_25" role="img" aria-label="Distance to dealer" />
              {isInStockOnline ? IN_STOCK_ONLINE : `${numberWithCommas(distance)} mi away`}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

MiniInventoryCard.propTypes = {
  vehicleParams: PropTypes.shape({
    makeSlug: PropTypes.string,
    modelSlug: PropTypes.string,
    year: PropTypes.string,
  }).isRequired,
  inventoryVehicle: InventoryEntities.InventoryVin.isRequired,
  radius: PropTypes.number,
  zipCode: PropTypes.string,
  isInternational: PropTypes.bool,
};

MiniInventoryCard.defaultProps = {
  radius: null,
  zipCode: null,
  isInternational: false,
};
